import {
  Anchor,
  Badge,
  Box,
  Card,
  Center,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  getHealth,
  getIngestionHealth,
  getMe,
  getProfiles,
  getStats,
  setProfileActive,
  type StatsResponse,
} from '../lib/api.js';

function formatUptime(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

export function Dashboard(): JSX.Element {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: getStats,
    refetchInterval: 30_000,
  });
  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
    refetchInterval: 15_000,
  });
  const { data: ingestion } = useQuery({
    queryKey: ['ingestion-health'],
    queryFn: getIngestionHealth,
    refetchInterval: 30_000,
  });
  const { data: me } = useQuery({ queryKey: ['auth-me'], queryFn: getMe });
  // Owner-only-Karten (Profile, LLM) hängen an der echten Identität — bleiben
  // dem Owner also auch während einer Impersonation erhalten.
  const isOwner = me?.realUser?.isOwner ?? me?.user.isOwner ?? false;

  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Title order={1}>Übersicht</Title>
        <Text c="dimmed" size="sm">
          Pipeline-Status auf einen Blick (Refresh alle 30 Sek)
        </Text>
      </Stack>

      {isLoading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      {stats && (
        <>
          {/* Highlight-Kacheln */}
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            <HeroStat
              label="Swipe-bereit"
              value={stats.swipeReady}
              accent="violet"
              to="/"
              hint="Phase 3 · zum Swipen"
            />
            <HeroStat label="Heute neu" value={stats.todayNew} accent="teal" />
            <HeroStat label="7 Tage" value={stats.last7dNew} />
            <HeroStat label="Objekte total" value={stats.totalObjects} />
          </SimpleGrid>

          {/* Globale Profil-Schalter wirken für alle User → nur Betreiber. */}
          {isOwner && <ProfilesCard />}

          {/* Pipeline-Funnel */}
          <Card padding="lg">
            <Stack gap="md">
              <Title order={4}>Pipeline</Title>
              <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
                <FunnelStep
                  icon="🔥"
                  label="Pipeline"
                  value={stats.funnel.new}
                  to="/list"
                  color="#7c3aed"
                />
                <FunnelStep
                  icon="🤝"
                  label="Partner-Check"
                  value={stats.funnel.partner_review}
                  to="/partner"
                  color="#d6336c"
                />
                <FunnelStep
                  icon="🎯"
                  label="Match"
                  value={stats.funnel.match}
                  to="/matches"
                  color="#f59e0b"
                />
                <FunnelStep
                  icon="✓"
                  label="In Kontakt"
                  value={stats.funnel.contacted}
                  to="/contacted"
                  color="#14b8a6"
                />
                <FunnelStep
                  icon="✗"
                  label="Abgelehnt"
                  value={stats.funnel.rejected}
                  to="/list"
                  color="#94a3b8"
                />
              </SimpleGrid>
            </Stack>
          </Card>

          {/* 7-Tage-Funnel: wie viel % der echten Neuzugänge (Maileingang) jede Phase überlebt */}
          <Card padding="lg">
            <Stack gap="md">
              <Group justify="space-between" align="baseline">
                <Title order={4}>7-Tage-Funnel</Title>
                <Text size="xs" c="dimmed">
                  echter Maileingang · letzte 7 Tage
                </Text>
              </Group>
              {stats.funnel7 && stats.funnel7.entered > 0 ? (
                <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
                  <Funnel7Tile label="Eingang" value={stats.funnel7.entered} base={stats.funnel7.entered} color="#64748b" />
                  <Funnel7Tile label="lebend" value={stats.funnel7.alive} base={stats.funnel7.entered} color="#0ea5e9" hint="nicht gekillt" />
                  <Funnel7Tile label="Phase 1 ✓" value={stats.funnel7.phase1} base={stats.funnel7.entered} color="#65a30d" hint="≥ Phase-1-Schwelle" />
                  <Funnel7Tile label="Phase 2 (KI)" value={stats.funnel7.phase2} base={stats.funnel7.entered} color="#eab308" hint="KI-Analyse gelaufen" />
                  <Funnel7Tile label="Phase 3 (Swipe)" value={stats.funnel7.phase3} base={stats.funnel7.entered} color="#7c3aed" hint="swipe-reif" />
                </SimpleGrid>
              ) : (
                <Text size="sm" c="dimmed">
                  Keine Neuzugänge in den letzten 7 Tagen.
                </Text>
              )}
            </Stack>
          </Card>

          {/* Score-Verteilung + Profil-Split */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Card padding="lg">
              <Stack gap="sm">
                <Title order={4}>Score-Verteilung (Pipeline)</Title>
                <ScoreBar buckets={stats.scoreBuckets} />
                <Stack gap={4}>
                  <LegendRow color="#0d9488" label="≥ 0.75 (top)" value={stats.scoreBuckets.ge75} />
                  <LegendRow color="#65a30d" label="0.65 – 0.75" value={stats.scoreBuckets.ge65} />
                  <LegendRow color="#eab308" label="0.50 – 0.65" value={stats.scoreBuckets.ge50} />
                  <LegendRow color="#f97316" label="< 0.50" value={stats.scoreBuckets.lt50} />
                  <LegendRow color="#ef4444" label="gekillt" value={stats.scoreBuckets.killed} />
                  <LegendRow color="#cbd5e1" label="ungescort" value={stats.scoreBuckets.unscored} />
                </Stack>
              </Stack>
            </Card>

            <Card padding="lg">
              <Stack gap="sm">
                <Title order={4}>Typ (Pipeline)</Title>
                <Group grow>
                  <ProfileStat
                    label="Miete"
                    value={stats.byDealTypeNew?.['miete'] ?? 0}
                    color="teal"
                  />
                  <ProfileStat
                    label="Kauf"
                    value={stats.byDealTypeNew?.['kauf'] ?? 0}
                    color="orange"
                  />
                </Group>
                <Title order={4} mt="md">
                  Quellen
                </Title>
                <Stack gap={6}>
                  {stats.perPortal.slice(0, 10).map((p) => (
                    <Group key={p.portal} justify="space-between">
                      <Text size="sm">{p.portal}</Text>
                      <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {p.count}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            </Card>
          </SimpleGrid>
        </>
      )}

      {/* System-Status */}
      {health && (
        <Card padding="lg">
          <Stack gap="md">
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Title order={4}>System</Title>
              <Group gap="md" align="center">
                <Anchor component={Link} to="/funnel" size="sm" fw={500}>
                  🎯 Funnel-Tuning →
                </Anchor>
                {isOwner && (
                  <Anchor component={Link} to="/llm" size="sm" fw={500}>
                    🔌 LLM-Status →
                  </Anchor>
                )}
                <Group gap={6} align="center">
                  <Box
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#10b981',
                    }}
                  />
                  <Text size="sm" c="dimmed">
                    {health.status} · Uptime {formatUptime(health.uptimeSec)}
                  </Text>
                </Group>
              </Group>
            </Group>
            {ingestion?.lastMail ? (
              <Group gap={6} wrap="wrap">
                <Text size="sm" c="dimmed">
                  Letzte Mail:
                </Text>
                <Badge size="sm" variant="light" tt="none">
                  {ingestion.lastMail.portal}
                </Badge>
                <Text size="sm">{ingestion.lastMail.subject}</Text>
                <Text size="xs" c="dimmed">
                  {new Date(ingestion.lastMail.receivedAt).toLocaleString('de-AT')}
                </Text>
              </Group>
            ) : (
              <Text c="dimmed" size="sm">
                Noch keine Mail empfangen.
              </Text>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

/** Saubere, konsistente Labels für die globalen Scoring-Rubriken (Deal-Typen). */
function rubricLabel(name: string): string {
  if (name === 'A_wohnung') return 'Miete';
  if (name === 'kauf') return 'Kauf';
  if (name === 'B_buero') return 'Büro';
  return name;
}

function ProfilesCard(): JSX.Element | null {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['profiles'],
    queryFn: getProfiles,
    refetchInterval: 30_000,
  });

  const toggleMut = useMutation({
    mutationFn: ({ name, active }: { name: string; active: boolean }) =>
      setProfileActive(name, active),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['profiles'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      qc.invalidateQueries({ queryKey: ['swipe-queue'] });
      qc.invalidateQueries({ queryKey: ['objects'] });
      notifications.show({
        message: `${rubricLabel(res.name)} ${res.active ? 'aktiviert' : 'pausiert'}`,
        color: res.active ? 'green' : 'orange',
      });
    },
    onError: (err) =>
      notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  if (!data) return null;

  const anyPaused = data.profiles.some((p) => !p.active);

  return (
    <Card padding="lg" style={anyPaused ? { borderLeft: '4px solid #f59e0b' } : undefined}>
      <Stack gap="md">
        <Group justify="space-between" align="baseline" wrap="wrap" gap="xs">
          <Title order={4}>Bewertungs-Engines</Title>
          {anyPaused && (
            <Badge size="sm" color="orange" variant="light" tt="none">
              ⏸ Eine Engine ist pausiert
            </Badge>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          Globale Scoring-Arten nach Deal-Typ (Miete / Kauf / Büro) — <b>nicht</b> deine
          Such-Profile. Pausiert: Mails werden weiter eingesammelt, aber nicht gescort/gepusht.
        </Text>
        {data.profiles.map((p) => (
          <Group key={p.name} justify="space-between" wrap="wrap" gap="sm">
            <Group gap={6} align="baseline">
              <Text fw={600}>{rubricLabel(p.name)}</Text>
              {!p.active && (
                <Badge size="xs" color="orange" variant="light" tt="none">
                  pausiert
                </Badge>
              )}
            </Group>
              <Switch
                size="md"
                checked={p.active}
                onChange={(e) =>
                  toggleMut.mutate({ name: p.name, active: e.currentTarget.checked })
                }
                disabled={toggleMut.isPending}
                onLabel="AN"
                offLabel="PAUSE"
                color="violet"
              />
          </Group>
        ))}
      </Stack>
    </Card>
  );
}

function HeroStat({
  label,
  value,
  accent,
  to,
  hint,
}: {
  label: string;
  value: number;
  accent?: 'violet' | 'teal';
  to?: string;
  hint?: string;
}): JSX.Element {
  const body = (
    <Card
      padding="lg"
      style={{
        height: '100%',
        background:
          accent === 'violet'
            ? 'linear-gradient(135deg, rgba(124,58,237,0.10), rgba(37,99,235,0.06))'
            : undefined,
        cursor: to ? 'pointer' : undefined,
      }}
    >
      <Stack gap={2}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.05em' }}>
          {label}
        </Text>
        <Text
          fw={800}
          style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.03em' }}
        >
          {value}
        </Text>
        {hint && (
          <Text size="xs" c="dimmed">
            {hint}
          </Text>
        )}
      </Stack>
    </Card>
  );
  return to ? (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {body}
    </Link>
  ) : (
    body
  );
}

function FunnelStep({
  icon,
  label,
  value,
  to,
  color,
}: {
  icon: string;
  label: string;
  value: number;
  to: string;
  color: string;
}): JSX.Element {
  return (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Card padding="md" style={{ borderLeft: `4px solid ${color}`, height: '100%' }}>
        <Stack gap={2}>
          <Text size="sm">
            {icon} {label}
          </Text>
          <Text fw={800} style={{ fontSize: '1.6rem', letterSpacing: '-0.02em' }}>
            {value}
          </Text>
        </Stack>
      </Card>
    </Link>
  );
}

function ScoreBar({ buckets }: { buckets: StatsResponse['scoreBuckets'] }): JSX.Element {
  const segs = [
    { v: buckets.ge75, c: '#0d9488' },
    { v: buckets.ge65, c: '#65a30d' },
    { v: buckets.ge50, c: '#eab308' },
    { v: buckets.lt50, c: '#f97316' },
    { v: buckets.killed, c: '#ef4444' },
    { v: buckets.unscored, c: '#cbd5e1' },
  ];
  const total = segs.reduce((s, x) => s + x.v, 0) || 1;
  return (
    <Group gap={0} wrap="nowrap" style={{ height: 16, borderRadius: 8, overflow: 'hidden' }}>
      {segs.map((s, i) =>
        s.v > 0 ? (
          <Box key={i} style={{ width: `${(s.v / total) * 100}%`, background: s.c, height: '100%' }} />
        ) : null,
      )}
    </Group>
  );
}

function LegendRow({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}): JSX.Element {
  return (
    <Group justify="space-between" gap="xs">
      <Group gap={8} wrap="nowrap">
        <Box style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
        <Text size="sm">{label}</Text>
      </Group>
      <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
    </Group>
  );
}

function ProfileStat({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}): JSX.Element {
  return (
    <Stack gap={2} align="center">
      <Badge color={color} variant="light" tt="none" size="lg">
        {label}
      </Badge>
      <Text fw={800} style={{ fontSize: '1.6rem' }}>
        {value}
      </Text>
    </Stack>
  );
}

function Funnel7Tile({
  label,
  value,
  base,
  color,
  hint,
}: {
  label: string;
  value: number;
  base: number;
  color: string;
  hint?: string;
}): JSX.Element {
  const pct = base > 0 ? Math.round((value / base) * 100) : 0;
  return (
    <Card padding="sm" style={{ borderTop: `3px solid ${color}`, height: '100%' }}>
      <Stack gap={2}>
        <Text size="xs" c="dimmed" fw={600} tt="none">
          {label}
        </Text>
        <Group gap={6} align="baseline" wrap="nowrap">
          <Text fw={800} style={{ fontSize: '1.5rem', letterSpacing: '-0.02em' }}>
            {value}
          </Text>
          <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {pct}%
          </Text>
        </Group>
        {hint && (
          <Text size="xs" c="dimmed">
            {hint}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
